import React, { useEffect, useState } from 'react'
import {
  Backdrop,
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  CircularProgress,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  FormControlLabel,
  Grid,
  MenuItem,
  Skeleton,
  Switch,
  TextField,
  Typography,
  Avatar,
} from '@mui/material'
import { ArrowBack, Delete, Extension, Save } from '@mui/icons-material'
import { Helmet } from 'react-helmet'
import { useNavigate, useParams } from 'react-router-dom'
import { useForm, Controller } from 'react-hook-form'
import { useSnackbar } from 'notistack'
import { api } from '../../../../services/Main/Api'

const slugify = (text) =>
  text
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/(^_|_$)/g, '')

const FeatureForm = () => {
  const { id } = useParams()
  const navigate = useNavigate()
  const { enqueueSnackbar } = useSnackbar()

  const isEdit = !!id
  const [isLoading, setIsLoading] = useState(isEdit)
  const [submitting, setSubmitting] = useState(false)
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)
  const [modules, setModules] = useState([])

  const { control, handleSubmit, watch, reset, setValue, formState: { errors } } = useForm({
    defaultValues: {
      name: '',
      slug: '',
      description: '',
      category: 'FEATURE',
      parent: '',
      is_active: true,
    },
  })

  const watchName = watch('name')
  const watchCategory = watch('category')

  useEffect(() => {
    if (!isEdit && watchName) {
      setValue('slug', slugify(watchName))
    }
  }, [watchName, isEdit, setValue])

  useEffect(() => {
    if (watchCategory === 'MODULE') {
      setValue('parent', '')
    }
  }, [watchCategory, setValue])

  useEffect(() => {
    fetchModules()
    if (isEdit) fetchFeature()
  }, [id])

  const fetchModules = async () => {
    try {
      const res = await api.get('/plan/features/', { params: { category: 'MODULE', page_size: 100 } })
      setModules(res.data.results || [])
    } catch (err) {
      console.error(err)
    }
  }

  const fetchFeature = async () => {
    try {
      const res = await api.get(`/plan/features/${id}/`)
      const data = res.data
      reset({
        name: data.name || '',
        slug: data.slug || '',
        description: data.description || '',
        category: data.category || 'FEATURE',
        parent: data.parent || '',
        is_active: data.is_active ?? true,
      })
    } catch (err) {
      enqueueSnackbar('Erro ao carregar funcionalidade', { variant: 'error' })
      navigate('/admin/funcionalidades')
    } finally {
      setIsLoading(false)
    }
  }

  const onSubmit = async (formData) => {
    try {
      setSubmitting(true)
      const payload = {
        ...formData,
        parent: formData.parent || null,
      }

      if (isEdit) {
        await api.put(`/plan/features/${id}/`, payload)
        enqueueSnackbar('Funcionalidade atualizada com sucesso', { variant: 'success' })
      } else {
        await api.post('/plan/features/', payload)
        enqueueSnackbar('Funcionalidade criada com sucesso', { variant: 'success' })
      }
      navigate('/admin/funcionalidades')
    } catch (err) {
      const errorMsg = err?.response?.data
      if (errorMsg && typeof errorMsg === 'object') {
        const firstError = Object.values(errorMsg).flat()[0]
        enqueueSnackbar(firstError || 'Erro ao salvar', { variant: 'error' })
      } else {
        enqueueSnackbar('Erro ao salvar funcionalidade', { variant: 'error' })
      }
    } finally {
      setSubmitting(false)
    }
  }

  const handleDelete = async () => {
    try {
      setSubmitting(true)
      await api.delete(`/plan/features/${id}/`)
      enqueueSnackbar('Funcionalidade excluída com sucesso', { variant: 'success' })
      navigate('/admin/funcionalidades')
    } catch (err) {
      enqueueSnackbar('Erro ao excluir funcionalidade', { variant: 'error' })
    } finally {
      setSubmitting(false)
      setDeleteDialogOpen(false)
    }
  }

  return (
    <>
      <Helmet>
        <title>{isEdit ? 'Editar' : 'Nova'} Funcionalidade - Admin</title>
      </Helmet>
      <Backdrop
        sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }}
        open={submitting}
      >
        <CircularProgress color="inherit" />
      </Backdrop>
      <Container maxWidth="md">
        <Box sx={{ mb: 3 }}>
          <Button
            startIcon={<ArrowBack />}
            onClick={() => navigate('/admin/funcionalidades')}
          >
            Voltar
          </Button>
          <Typography variant="h5" fontWeight="bold" sx={{ mt: 1 }}>
            {isEdit ? 'Editar' : 'Nova'} Funcionalidade
          </Typography>
        </Box>

        <Card>
          {isLoading ? (
            <Skeleton variant="rounded" height={500} animation="pulse" sx={{ m: 2 }} />
          ) : (
            <>
              <CardHeader
                avatar={
                  <Avatar sx={{ bgcolor: 'primary.main' }}>
                    <Extension />
                  </Avatar>
                }
                title={isEdit ? 'Editar Funcionalidade' : 'Nova Funcionalidade'}
              />
              <Divider />
              <CardContent>
                <form onSubmit={handleSubmit(onSubmit)}>
                  <Grid container spacing={2} rowSpacing={3}>
                    <Grid item xs={12} md={6}>
                      <Controller
                        name="name"
                        control={control}
                        rules={{ required: 'Nome é obrigatório' }}
                        render={({ field }) => (
                          <TextField
                            {...field}
                            label="Nome"
                            fullWidth
                            size="small"
                            required
                            error={!!errors.name}
                            helperText={errors.name?.message}
                          />
                        )}
                      />
                    </Grid>

                    <Grid item xs={12} md={6}>
                      <Controller
                        name="slug"
                        control={control}
                        rules={{ required: 'Slug é obrigatório' }}
                        render={({ field }) => (
                          <TextField
                            {...field}
                            label="Slug"
                            fullWidth
                            size="small"
                            required
                            error={!!errors.slug}
                            helperText={errors.slug?.message || 'Identificador único (auto-gerado)'}
                            InputProps={{
                              sx: { fontFamily: 'monospace' },
                            }}
                          />
                        )}
                      />
                    </Grid>

                    <Grid item xs={12}>
                      <Controller
                        name="description"
                        control={control}
                        render={({ field }) => (
                          <TextField
                            {...field}
                            label="Descrição"
                            fullWidth
                            size="small"
                            multiline
                            rows={3}
                          />
                        )}
                      />
                    </Grid>

                    <Grid item xs={12} md={6}>
                      <Controller
                        name="category"
                        control={control}
                        rules={{ required: 'Categoria é obrigatória' }}
                        render={({ field }) => (
                          <TextField
                            {...field}
                            select
                            label="Categoria"
                            fullWidth
                            size="small"
                            required
                            error={!!errors.category}
                            helperText={errors.category?.message}
                          >
                            <MenuItem value="MODULE">Módulo</MenuItem>
                            <MenuItem value="FEATURE">Funcionalidade</MenuItem>
                          </TextField>
                        )}
                      />
                    </Grid>

                    {watchCategory === 'FEATURE' && (
                      <Grid item xs={12} md={6}>
                        <Controller
                          name="parent"
                          control={control}
                          render={({ field }) => (
                            <TextField
                              {...field}
                              select
                              label="Módulo Pai"
                              fullWidth
                              size="small"
                              helperText="Opcional - vincule a um módulo"
                            >
                              <MenuItem value="">Nenhum</MenuItem>
                              {modules.map((mod) => (
                                <MenuItem key={mod.id} value={mod.id}>
                                  {mod.name}
                                </MenuItem>
                              ))}
                            </TextField>
                          )}
                        />
                      </Grid>
                    )}

                    <Grid item xs={12} md={6}>
                      <Controller
                        name="is_active"
                        control={control}
                        render={({ field }) => (
                          <FormControlLabel
                            control={
                              <Switch
                                checked={field.value}
                                onChange={(e) => field.onChange(e.target.checked)}
                                color="primary"
                              />
                            }
                            label="Ativo"
                          />
                        )}
                      />
                    </Grid>
                  </Grid>

                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 4 }}>
                    {isEdit ? (
                      <Button
                        variant="outlined"
                        color="error"
                        startIcon={<Delete />}
                        onClick={() => setDeleteDialogOpen(true)}
                      >
                        Excluir
                      </Button>
                    ) : (
                      <Box />
                    )}
                    <Button
                      type="submit"
                      variant="contained"
                      startIcon={<Save />}
                      disabled={submitting}
                    >
                      {isEdit ? 'Salvar' : 'Criar'}
                    </Button>
                  </Box>
                </form>
              </CardContent>
            </>
          )}
        </Card>

        <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
          <DialogTitle>Confirmar exclusão</DialogTitle>
          <DialogContent>
            <DialogContentText>
              Tem certeza que deseja excluir esta funcionalidade? Esta ação não pode ser desfeita.
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDeleteDialogOpen(false)}>Cancelar</Button>
            <Button onClick={handleDelete} color="error" variant="contained">
              Excluir
            </Button>
          </DialogActions>
        </Dialog>
      </Container>
    </>
  )
}

export default FeatureForm
